import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useI18n } from '../contexts/I18nContext';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const { cartItems, getCartTotal, clearCart } = useCart();
  const { subtotal, shipping, total } = getCartTotal();
  const [paymentMethod, setPaymentMethod] = useState('online');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);

  useEffect(() => {
    // Only redirect to cart if cart is empty AND order hasn't been placed
    if (cartItems.length === 0 && !orderPlaced) {
      navigate('/cart');
    }
  }, [cartItems, navigate, orderPlaced]);

  const handlePlaceOrder = async () => {
    setLoading(true);
    try {
      const shippingAddress = `${user.address}, ${user.postal_code} ${user.city}`;
      const orderData = {
        items: cartItems.map((item) => ({
          product_id: item.product.id,
          quantity: item.quantity,
        })),
        payment_method: paymentMethod,
        shipping_address: shippingAddress,
        notes: notes || undefined,
        origin_url: window.location.origin,
      };

      const response = await api.post('/orders', orderData);

      if (paymentMethod === 'online' && response.data.checkout_url) {
        setOrderPlaced(true);
        clearCart();
        window.location.href = response.data.checkout_url;
      } else {
        setOrderPlaced(true);
        clearCart();
        toast.success(t('orderSuccess'));
        navigate('/orders');
      }
    } catch (error) {
      console.error('Order failed', error);
      toast.error(t('orderFailed'));
    } finally {
      setLoading(false);
    }
  };

  const WHATSAPP_PHONE = '491716295816'; // ED STEEL phone number (without + and spaces)

  const sendWhatsAppOrder = () => {
    // Build order message in German (documents always in German per requirements)
    const itemsList = cartItems.map(item => 
      `• ${item.product.name} x${item.quantity} - €${(item.product.price * item.quantity).toFixed(2)}`
    ).join('\n');
    
    const message = `🛒 *Neue Bestellung - ED STEEL*

*Firma:* ${user.company_name}
*Kontakt:* ${user.contact_name}
*Tel:* ${user.phone}

*Lieferadresse:*
${user.address}
${user.postal_code} ${user.city}

*Bestellte Artikel:*
${itemsList}

*Zwischensumme:* €${subtotal.toFixed(2)}
*Versand:* ${shipping > 0 ? `€${shipping.toFixed(2)}` : 'Kostenlos'}
*Gesamtbetrag:* €${total.toFixed(2)}

*Zahlungsmethode:* ${paymentMethod === 'online' ? 'Online-Zahlung' : 'Nachnahme'}
${notes ? `\n*Anmerkungen:* ${notes}` : ''}

Vielen Dank!`;

    window.open(`https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="container mx-auto px-4 lg:px-12 py-8">
      <h1 className="text-3xl md:text-4xl font-heading font-bold mb-8 uppercase" data-testid="checkout-title">
        {t('checkout')}
      </h1>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {/* Shipping Address */}
          <div className="bg-white border border-border rounded-md p-6">
            <h2 className="text-xl font-heading font-bold mb-4">{t('shippingAddress')}</h2>
            <p className="text-muted-foreground">
              {user.company_name}<br />
              {user.contact_name}<br />
              {user.address}<br />
              {user.postal_code} {user.city}<br />
              {user.phone}
            </p>
          </div>

          {/* Payment Method */}
          <div className="bg-white border border-border rounded-md p-6">
            <h2 className="text-xl font-heading font-bold mb-4">{t('paymentMethod')}</h2>
            <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
              <div className="flex items-center space-x-2 mb-3">
                <RadioGroupItem value="online" id="online" data-testid="payment-online" />
                <Label htmlFor="online" className="cursor-pointer">{t('onlinePayment')}</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="cash" id="cash" data-testid="payment-cash" />
                <Label htmlFor="cash" className="cursor-pointer">{t('payOnDelivery')}</Label>
              </div>
            </RadioGroup>
          </div>

          {/* Notes */}
          <div className="bg-white border border-border rounded-md p-6">
            <h2 className="text-xl font-heading font-bold mb-4">{t('notes')}</h2>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('notesPlaceholder')}
              rows={3}
              data-testid="order-notes-input"
            />
          </div>

          {/* WhatsApp Alternative */}
          <Button
            variant="outline"
            onClick={sendWhatsAppOrder}
            className="w-full bg-green-50 border-green-500 text-green-700 hover:bg-green-100"
            data-testid="whatsapp-button"
          >
            <MessageSquare className="h-5 w-5 me-2" />
            {t('orderViaWhatsApp')}
          </Button>
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <div className="bg-muted p-6 rounded-md">
            <h2 className="text-xl font-heading font-bold mb-4">{t('orderSummary')}</h2>

            <div className="space-y-3 mb-6">
              {cartItems.map((item) => (
                <div key={item.product.id} className="flex justify-between text-sm">
                  <span>
                    {item.product.name} x{item.quantity}
                  </span>
                  <span>€{(item.product.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>

            <div className="space-y-3 border-t border-border pt-4">
              <div className="flex justify-between">
                <span>{t('subtotal')}</span>
                <span className="font-semibold">€{subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>{t('shipping')}</span>
                <span className="font-semibold">
                  {shipping === 0 ? t('freeShipping') : `€${shipping.toFixed(2)}`}
                </span>
              </div>
              <div className="border-t border-border pt-3 flex justify-between text-lg">
                <span className="font-bold">{t('total')}</span>
                <span className="font-bold">€{total.toFixed(2)}</span>
              </div>
            </div>

            <Button
              onClick={handlePlaceOrder}
              disabled={loading}
              className="w-full h-12 text-lg mt-6 bg-gold hover:bg-gold/90 text-primary font-heading"
              data-testid="place-order-button"
            >
              {loading ? t('processing') : t('placeOrder')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;